import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import './GanttPage.css';
import { useTaskStore } from '../stores/pmTaskStore';
import { refetchProjectItems } from '../stores/createProjectItemStore';
import { updateTask } from '../services/api';
import { toast } from 'react-hot-toast';
import ProjectHeader from '../components/ProjectHeader';
import EmptyPanel from '../components/EmptyPanel';
import { useProject } from '../hooks/useProject';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { toDateInput, toISOString } from '../utils/dateUtils';
import { useRememberedState } from '@redpanda/utils';

type ViewMode = 'day' | 'week' | 'month';
const VIEW_MODES: ViewMode[] = ['day', 'week', 'month'];

const DAY_MS = 24 * 60 * 60 * 1000;

/** 本地日期 YYYY-MM-DD（toISOString() 是 UTC，台灣早上 8 點前會差一天） */
const localDateStr = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const GanttPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  // 用 state 保存容器節點：容器在條件渲染後才出現，節點出現時才觸發初始化（callback ref，見 9-0 踩坑紀錄）
  const [ganttContainer, setGanttContainer] = useState<HTMLDivElement | null>(null);
  const [viewMode, setViewMode] = useRememberedState<ViewMode>('pm-ui:gantt.viewMode', 'month', VIEW_MODES);

  const returnTab = new URLSearchParams(location.search).get('returnTab') || undefined;

  const { project, loading: projectLoading } = useProject(id);
  const { items: allTasks, update: updateTaskInStore } = useTaskStore();
  const projectTasks = useMemo(() => allTasks.filter(task => task.projectId === id), [allTasks, id]);

  // 拖曳改截止日時 store 已更新，通知其他頁時略過自己的重抓（否則甘特圖會再重建一次）
  const skipOwnRefresh = useRef(false);

  // 進頁面時任務由 useProject 一起抓回（GET /projects/:id 帶子資料）；之後別頁改了任務再單獨重抓
  useDataChanged('tasks', () => {
    if (!skipOwnRefresh.current && id) refetchProjectItems(useTaskStore, 'tasks', id).catch(console.error);
  });

  // 轉換為 DHTMLX Gantt 格式（只顯示有截止日期的任務；開始日＝建立日，截止日當天也算在長條內）
  // 截止日早於建立日（補登過去的任務）時從截止日開始畫 1 天：否則長條畫在建立日，一拖動就把截止日改成建立日
  const ganttData = useMemo(() => {
    const data = projectTasks
      .filter(task => task.dueDate)
      .map(task => {
        const due = toDateInput(task.dueDate);
        const created = localDateStr(task.createdAt ? new Date(task.createdAt) : new Date());
        const start = created <= due ? created : due;
        return {
          id: task.id,
          text: task.title,
          start_date: start,
          duration: Math.round((Date.parse(due) - Date.parse(start)) / DAY_MS) + 1,
          status: task.status,
        };
      });
    return { data, links: [] };
  }, [projectTasks]);

  const isLoading = projectLoading && projectTasks.length === 0;

  // 時間尺度
  const setScaleConfig = (mode: ViewMode) => {
    const cjk = i18n.language === 'zh' || i18n.language === 'ja';
    switch (mode) {
      case 'day':
        gantt.config.scales = [{ unit: 'day', step: 1, format: '%m/%d' }];
        gantt.config.scale_height = 40;
        gantt.config.min_column_width = 80;
        break;
      case 'week':
        gantt.config.scales = cjk
          ? [{ unit: 'week', step: 1, format: '第%W週' }, { unit: 'day', step: 1, format: '%d日' }]
          : [{ unit: 'week', step: 1, format: 'Week %W' }, { unit: 'day', step: 1, format: '%d' }];
        gantt.config.scale_height = 56;
        gantt.config.min_column_width = 50;
        break;
      case 'month':
        gantt.config.scales = cjk
          ? [{ unit: 'month', step: 1, format: '%Y年%m月' }, { unit: 'day', step: 1, format: '%d' }]
          : [{ unit: 'month', step: 1, format: '%M %Y' }, { unit: 'day', step: 1, format: '%d' }];
        gantt.config.scale_height = 56;
        gantt.config.min_column_width = 30;
        break;
    }
  };

  // 初始化 Gantt
  useEffect(() => {
    if (!ganttContainer || ganttData.data.length === 0) return;

    // 外觀由 GanttPage.css 用語意 token 覆寫（深淺主題、主題色自動切換），不再寫死深色 skin（§3-11）
    gantt.plugins({ marker: true });
    gantt.config.date_format = '%Y-%m-%d';
    gantt.config.readonly = false;
    gantt.config.drag_move = true;
    gantt.config.drag_resize = true;
    gantt.config.drag_progress = false;
    gantt.config.show_progress = false;
    gantt.config.show_grid = true;
    gantt.config.show_links = false;
    gantt.config.fit_tasks = true;
    gantt.config.autosize = 'y';
    gantt.config.autoscroll = true;
    gantt.config.row_height = 40;
    gantt.config.bar_height = 16;
    gantt.config.drag_timeline = { useKey: false, ignore: '.gantt_task_line' };

    // 長條：主色＝進行中、灰＝未開始、綠＝完成、紅＝受阻；長條內不放文字（標題在左欄）
    gantt.templates.task_class = (_start, _end, task) => `pm-bar pm-bar--${String(task.status || 'PENDING').toLowerCase()}`;
    gantt.templates.task_text = () => '';
    // 週末底色淡一階
    gantt.templates.timeline_cell_class = (_item, date) => (date.getDay() === 0 || date.getDay() === 6 ? 'pm-weekend' : '');

    // 拖拽後同步 dueDate 至後端（end_date 是結束後一天，減一天＝截止日）
    const dragEventId = gantt.attachEvent('onAfterTaskDrag', (taskId: string) => {
      const task = gantt.getTask(taskId);
      if (!task?.end_date) return;
      const due = toISOString(localDateStr(new Date(task.end_date.getTime() - DAY_MS)))!;
      const previous = useTaskStore.getState().items.find(item => item.id === taskId)?.dueDate;
      if (toDateInput(previous) === toDateInput(due)) return; // 只拖了開始日、或放回原位：截止日沒變
      updateTaskInStore(taskId, { dueDate: due });
      updateTask(taskId, { dueDate: due })
        .then(() => {
          skipOwnRefresh.current = true;
          notifyDataChanged('tasks');
          skipOwnRefresh.current = false;
        })
        .catch(() => {
          updateTaskInStore(taskId, { dueDate: previous });
          toast.error(t('pmTasks.updateError', { ns: 'pages' }));
        });
    });

    setScaleConfig(viewMode);

    gantt.config.columns = [
      { name: 'text', label: t('pmTasks.fields.title', { ns: 'pages' }), tree: true, width: '*', resize: true },
      { name: 'start_date', label: t('common.table.columns.startDate'), align: 'center', width: 100, resize: true },
      { name: 'duration', label: t('pmGantt.duration', { ns: 'pages' }), align: 'center', width: 60, resize: true },
    ];

    gantt.init(ganttContainer);
    gantt.clearAll();
    // 今日線（主色 1px）
    const todayMarkerId = gantt.addMarker({ start_date: new Date(), css: 'pm-today', title: t('pmGantt.today', { ns: 'pages' }) });
    gantt.parse(ganttData);

    return () => {
      // 每次重新初始化都會綁一次事件、加一條今日線；不解除的話拖一次會送出多個 PATCH
      gantt.detachEvent(dragEventId);
      gantt.deleteMarker(String(todayMarkerId));
      gantt.clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ganttData, t, ganttContainer]);

  // 切換日／週／月或語言時只更新尺度
  useEffect(() => {
    if (!ganttContainer || ganttData.data.length === 0) return;
    setScaleConfig(viewMode);
    gantt.render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, ganttData.data.length, i18n.language, ganttContainer]);

  if (!id) return null;

  const modeClass = (active: boolean) =>
    `inline-flex items-center rounded px-3 py-1.5 text-sm transition-colors ${
      active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'
    }`;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <ProjectHeader projectId={id} project={project} current="gantt" returnTab={returnTab} />

      {/* 日／週／月切換與操作提示（與其他頁的搜尋列同一個位置、同高） */}
      {ganttData.data.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <nav aria-label={t('pmGantt.viewMode.label', { ns: 'pages' })} className="inline-flex h-10 items-center rounded-md border border-line bg-surface p-0.5">
            {VIEW_MODES.map(mode => (
              <button key={mode} type="button" onClick={() => setViewMode(mode)} aria-pressed={viewMode === mode} className={modeClass(viewMode === mode)}>
                {t(`pmGantt.viewMode.${mode}`, { ns: 'pages' })}
              </button>
            ))}
          </nav>
          <p className="text-[13px] text-fg-secondary">{t('pmGantt.dragHint', { ns: 'pages' })}</p>
        </div>
      )}

      {isLoading ? (
        <div className="h-64 rounded-lg border border-line bg-surface animate-pulse motion-reduce:animate-none" />
      ) : ganttData.data.length === 0 ? (
        <EmptyPanel title={t('pmGantt.noData', { ns: 'pages' })} hint={t('pmGantt.emptyHint', { ns: 'pages' })} />
      ) : (
        <div className="pm-gantt rounded-lg border border-line overflow-hidden">
          <div ref={setGanttContainer} style={{ width: '100%', minHeight: '320px' }} />
        </div>
      )}
    </div>
  );
};

export default GanttPage;
